import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import { Star, User, Calendar, MessageSquare, RefreshCw, MapPin, Award, Map, Edit3 } from 'lucide-react';

const GooglePlacesReviews = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  const [reviews, setReviews] = useState([]);
  const [placeInfo, setPlaceInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Google Maps and Review URLs
  const GOOGLE_MAPS_URL = "https://www.google.com/maps/place/Century+-+Longevity+%26+Aesthetics/@40.722696,-73.8571024,854m/data=!3m2!1e3!4b1!4m6!3m5!1s0x89c25f3efd11ec71:0x59b5c67e4f9c82e!8m2!3d40.722692!4d-73.8522315!16s%2Fg%2F11msbj1tpy!5m1!1e2?entry=ttu";
  const GOOGLE_REVIEW_URL = "https://www.google.com/search?sca_esv=262b8e8005cfc964&sxsrf=ANbL-n4lY4UU7AdooXVeTcDW0_rBmgYAWQ:1769410631683&si=AL3DRZEsmMGCryMMFSHJ3StBhOdZ2-6yYkXd_doETEE1OR-qOXXEkjllLSgD9EC-CKrBL4tRqRU27wqseYL3EcbYpiAmkaMLiy6Mn00FS3W4fgkBfy_B1ZZBlOEmHSiPtsYul6YLs8BJ1EzRxIXYj8A4-iiZ07LmNxOU-BdgwZMh5uZLIjECJA0%3D&q=Century+-+Longevity+%26+Aesthetics+Reviews&sa=X&ved=2ahUKEwjc-6ah0KiSAxV-v4kEHQmSHbIQ0bkNegQIJxAH&biw=1512&bih=945&dpr=2&aic=0#lrd=0x89c25f3efd11ec71:0x59b5c67e4f9c82e,3,,,,";

  // Fetch reviews on component mount
  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    setLoading(true);
    setError(null);

    try {
      // Determine API URL based on environment
      const isProduction = window.location.hostname.includes('vercel.app') || window.location.hostname.includes('centuryaesthetics.org');
      const API_URL = isProduction 
        ? 'https://aesthetics-backend-ajgb.vercel.app'
        : (import.meta.env.VITE_API_URL || 'http://localhost:5001');
      
      console.log('🔍 Fetching reviews from:', API_URL);
      console.log('🌍 Environment:', isProduction ? 'Production' : 'Development');
      
      const response = await fetch(`${API_URL}/api/reviews`);

      if (!response.ok) {
        throw new Error(`Server error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log('✅ Received data:', data);

      if (data.status === 'OK') {
        setPlaceInfo({
          name: data.result.name,
          rating: data.result.rating,
          totalRatings: data.result.user_ratings_total,
          address: data.result.formatted_address,
          phone: data.result.formatted_phone_number
        });
        setReviews(data.result.reviews || []);
        console.log('✅ Reviews loaded successfully:', data.result.reviews?.length || 0);
      } else {
        throw new Error(data.error_message || data.error || 'Failed to fetch reviews');
      }
    } catch (err) {
      setError(err.message);
      console.error('❌ Error fetching reviews:', err);
    } finally {
      setLoading(false);
    }
  };

  // Render star rating
  const StarRating = ({ rating }) => (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`w-4 h-4 ${
            star <= Math.floor(rating) ? 'fill-gold text-gold' : 
            star === Math.ceil(rating) && rating % 1 !== 0 ? 'fill-gold/50 text-gold' :
            'text-olive-medium/30'
          }`}
        />
      ))}
    </div>
  );

  // Format relative time
  const formatRelativeTime = (timestamp) => {
    const now = Date.now() / 1000;
    const diff = now - timestamp;
    const days = Math.floor(diff / 86400);
    
    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days} days ago`;
    if (days < 30) return `${Math.floor(days / 7)} weeks ago`;
    if (days < 365) return `${Math.floor(days / 30)} months ago`;
    return `${Math.floor(days / 365)} years ago`;
  };

  return (
    <section id="reviews" ref={ref} className="py-36 bg-white">
      <div className="max-w-7xl mx-auto px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="font-heading text-5xl font-semibold text-olive-darkest mb-3">
            Client Reviews
          </h2>
          <div className="w-20 h-0.5 bg-gold mx-auto mb-4"></div>
          <p className="text-text-dark text-base opacity-80 max-w-3xl mx-auto">
            See what our clients are saying about their experience with Century Aesthetics & Longevity
          </p>
        </motion.div>

        {/* Business Info Card */}
        {placeInfo && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-cream rounded-lg shadow-md p-8 mb-10"
          >
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
              <div className="flex-1">
                <h3 className="font-heading text-2xl font-semibold text-olive-darkest mb-2">
                  {placeInfo.name}
                </h3>
                {placeInfo.address && (
                  <div className="flex items-start gap-2 text-text-dark opacity-80 mb-3">
                    <MapPin className="w-4 h-4 mt-1 flex-shrink-0 text-olive-dark" />
                    <p className="text-sm">{placeInfo.address}</p>
                  </div>
                )}
                <div className="flex items-center gap-4 flex-wrap">
                  <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg shadow-sm">
                    <Award className="w-5 h-5 text-gold" />
                    <StarRating rating={placeInfo.rating} />
                    <span className="text-lg font-semibold text-olive-darkest ml-1">
                      {placeInfo.rating.toFixed(1)}
                    </span>
                  </div>
                  <span className="text-text-dark opacity-80 text-sm">
                    Based on {placeInfo.totalRatings} reviews
                  </span>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <motion.a
                  href={GOOGLE_REVIEW_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  className="bg-gold text-olive-darkest px-6 py-3 rounded font-medium tracking-wide hover:bg-olive-darkest hover:text-cream transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
                >
                  <Edit3 className="w-4 h-4" />
                  Write a Review
                </motion.a>
                <motion.a
                  href={GOOGLE_MAPS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  className="bg-white text-olive-darkest border-2 border-olive-darkest px-6 py-3 rounded font-medium tracking-wide hover:bg-olive-darkest hover:text-cream transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
                >
                  <Map className="w-4 h-4" />
                  View on Map
                </motion.a>
                <motion.button
                  whileHover={{ y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={fetchReviews}
                  disabled={loading}
                  className="bg-olive-darkest text-cream px-6 py-3 rounded font-medium tracking-wide hover:bg-gold hover:text-olive-darkest transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                  {loading ? 'Loading...' : 'Refresh'}
                </motion.button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Error Message */}
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="bg-red-50 border border-red-200 text-red-800 px-6 py-4 rounded-lg mb-8"
            >
              <p className="font-semibold text-sm mb-1">Error Loading Reviews</p>
              <p className="text-sm opacity-80">{error}</p>
              <p className="text-xs mt-2 opacity-70">
                Please try refreshing the page or contact support if the issue persists.
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Loading State */}
        {loading && reviews.length === 0 && (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="bg-cream rounded-lg shadow-md p-6 animate-pulse">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-olive-medium/20 rounded-full"></div>
                  <div className="flex-1">
                    <div className="h-4 bg-olive-medium/20 rounded w-3/4 mb-2"></div>
                    <div className="h-3 bg-olive-medium/20 rounded w-1/2"></div>
                  </div>
                </div>
                <div className="h-4 bg-olive-medium/20 rounded w-1/3 mb-4"></div>
                <div className="space-y-2">
                  <div className="h-3 bg-olive-medium/20 rounded"></div>
                  <div className="h-3 bg-olive-medium/20 rounded"></div>
                  <div className="h-3 bg-olive-medium/20 rounded w-5/6"></div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Reviews Grid */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          <AnimatePresence>
            {reviews.map((review, index) => (
              <motion.div
                key={review.time || index}
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                exit={{ opacity: 0, y: 20 }}
                transition={{ duration: 0.6, delay: 0.1 * index }}
                whileHover={{ y: -5 }}
                className="bg-cream rounded-lg shadow-md hover:shadow-xl transition-all duration-300 p-6"
              >
                {/* Reviewer Info */}
                <div className="flex items-center gap-3 mb-4">
                  {review.profile_photo_url ? (
                    <img
                      src={review.profile_photo_url}
                      alt={review.author_name}
                      className="w-12 h-12 rounded-full object-cover border-2 border-olive-medium/20"
                    />
                  ) : (
                    <div className="w-12 h-12 bg-olive-darkest rounded-full flex items-center justify-center">
                      <User className="w-6 h-6 text-cream" />
                    </div>
                  )}
                  <div className="flex-1">
                    <p className="font-semibold text-olive-darkest text-sm">
                      {review.author_name}
                    </p>
                    <div className="flex items-center gap-2 text-xs text-text-dark opacity-70">
                      <Calendar className="w-3 h-3" />
                      {formatRelativeTime(review.time)}
                    </div>
                  </div>
                </div>

                {/* Rating */}
                <div className="mb-4">
                  <StarRating rating={review.rating} />
                </div>

                {/* Review Text */}
                {review.text && (
                  <div className="mb-4">
                    <div className="flex items-start gap-2">
                      <MessageSquare className="w-4 h-4 text-olive-dark flex-shrink-0 mt-1 opacity-60" />
                      <p className="text-text-dark text-sm leading-relaxed opacity-90">
                        {review.text}
                      </p>
                    </div>
                  </div>
                )}

                {/* Review Source Link */}
                {review.author_url && (
                  <a
                    href={review.author_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-olive-dark hover:text-gold text-xs font-medium inline-flex items-center gap-1 mt-2 transition-colors duration-300"
                  >
                    View on Google →
                  </a>
                )}
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        {/* Empty State */}
        {!loading && reviews.length === 0 && !error && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-cream rounded-lg shadow-md p-12 text-center"
          >
            <MessageSquare className="w-16 h-16 text-olive-medium/40 mx-auto mb-4" />
            <h3 className="font-heading text-2xl font-semibold text-olive-darkest mb-2">
              No reviews yet
            </h3>
            <p className="text-text-dark opacity-80 text-sm">
              Check back later for customer reviews
            </p>
          </motion.div>
        )}
      </div>
    </section>
  );
};

export default GooglePlacesReviews;